// Creative-library picker backed by /creatives and /productions.

import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import type { Creative } from '@/api/types';
import { SearchableDropdown } from '@/components/dropdowns';
import { useCreatives, useProductions } from '@/api/hooks';
import { useQueries } from '@tanstack/react-query';
import { creativeRevisions } from '@/api/resources';
import { pinnedCreativeSnapshots } from '@/lib/campaign-creative-pins';
import { QueryErrorNote } from '@/components/error-panel';
import {
  RequiredField,
  useRequiredFeedback,
} from '@/components/required-field';
import { libraryToAssets, type LibraryPreviewAsset } from '@/lib/creative-library-assets';
import { creativeSpecDisplay } from './creative-config';
import { orderSelectedFirst } from '@/lib/selection-order';

// LibraryPicker — modal to select creatives from the Creative Library
// Used by Meta, CTV, Display creative builders.

/// Same resolution the library grid uses: the 1:1 for Meta, otherwise the first
/// attached asset. Returns null when `view_url` is absent so callers fall back.
function pickerPreview(creative: Creative): LibraryPreviewAsset | null {
  if (creative.channel === 'meta') {
    const assets = libraryToAssets(creative, null, 'meta');
    return assets.sq || assets.fs || assets.story || null;
  }
  if (creative.channel === 'ctv') return libraryToAssets(creative, null, 'ctv').asset || null;
  // A Display banner is itself the preview record; it has no `.asset`, which is
  // what this read, so every Display creative in the picker showed its fallback.
  return libraryToAssets(creative, null, 'dsp').banners.find(banner => !!banner.url) || null;
}

export interface LibraryPickerProps {
  open: boolean;
  /** Only this channel's creatives are offered. */
  channel: Creative['channel'];
  onClose: () => void;
  /** The picked creatives; an older approved version is handed over as that version. */
  onPick: (creatives: Creative[]) => void;
  /** The builder's production, which seeds the production filter. */
  currentShow?: { id: string } | null;
  multi?: boolean;
}

function LibraryPicker({ open, channel, onClose, onPick, currentShow, multi = false }: LibraryPickerProps) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>('approved');
  const [productionFilter, setProductionFilter] = useState<string>(currentShow?.id || 'all');
  // The picks as they were picked, in the order picked: a creative stays picked
  // (as its approved revision) when a later search hides it.
  const [selected, setSelected] = useState<ReadonlyMap<string, Creative>>(new Map());
  const required = useRequiredFeedback();
  // Cards are ordered selected-first by the picks as they were when the
  // filters last changed (or the picker opened), so a card never moves while
  // it is being clicked (`lib/selection-order.ts`).
  const [pinned, setPinned] = useState<{ key: string; ids: ReadonlySet<string> }>(
    () => ({ key: '', ids: new Set() }),
  );
  const orderRef = useRef<{ key: string; ids: readonly string[] }>({ key: '', ids: [] });
  // Every opening computes its order afresh, even when nothing in its key
  // moved since the last one (a rename while it was open last time).
  const openRef = useRef(open);
  if (openRef.current !== open) {
    openRef.current = open;
    orderRef.current = { key: '', ids: [] };
  }
  // Hooks must run unconditionally — call before the open early-return.
  const { data: productions = [] } = useProductions();
  const { data: creatives = [] } = useCreatives();
  useEffect(() => {
    if (open) {
      setSelected(new Map());
      // A fresh opening orders by its own (empty) selection, whatever the
      // filters read when the picker last closed.
      setPinned({ key: '', ids: new Set() });
      setQ('');
      setProductionFilter(currentShow?.id || 'all');
      required.clearAll();
    }
  }, [open, currentShow]);
  const productionMap = Object.fromEntries(productions.map(s => [s.id, s]));
  const candidates = creatives.filter(c => {
    if (channel && c.channel !== channel) return false;
    // "Approved" means "has an approved version a campaign can fly": a v3
    // draft over an approved v2 still qualifies, and the pick pins v2.
    if (status === 'approved' ? c.approved_revision == null : (status !== 'all' && c.status !== status)) return false;
    // c.production_id is the canonical production foreign key.
    if (productionFilter !== 'all' && c.production_id !== productionFilter) return false;
    if (q.trim() && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const olderApproved = open ? candidates.filter((creative) =>
    creative.approved_revision != null && creative.approved_revision !== creative.revision) : [];
  const archives = useQueries({ queries: olderApproved.map((creative) => creativeRevisions(creative.id)) });
  const filtered = candidates.flatMap((creative) => {
    const index = olderApproved.findIndex((candidate) => candidate.id === creative.id);
    // `olderApproved` holds only creatives with an approved revision.
    if (index < 0 || creative.approved_revision == null) return [creative];
    // Every builder pins approved_revision. Its preview, copy and uploaded
    // media must therefore come from that revision, not a newer draft.
    return pinnedCreativeSnapshots([
      { creative_id: creative.id, creative_revision: creative.approved_revision },
    ], [creative], [archives[index]?.data]);
  });
  const filterKey = [q, status, productionFilter].join('\u0000');
  if (pinned.key !== filterKey) setPinned({ key: filterKey, ids: new Set(selected.keys()) });
  // The card order is frozen with the snapshot: a creative renamed by someone
  // else while the picker is open keeps its place. Only a change in which
  // cards exist computes the order again.
  // Which cards exist, not the order they arrive in: a refetch that re-sorts a
  // renamed creative must not count as a new list.
  const membership = filtered.map((c) => c.id).sort().join(',');
  const orderKey = `${filterKey}\u0000${[...pinned.ids].join(',')}\u0000${membership}`;
  if (orderRef.current.key !== orderKey) {
    orderRef.current = {
      key: orderKey,
      ids: orderSelectedFirst(filtered, pinned.ids, 'alpha', (c) => c.id, (c) => c.name).map((c) => c.id),
    };
  }
  const byId = new Map(filtered.map((c) => [c.id, c]));
  const shown = orderRef.current.ids.flatMap((id) => {
    const card = byId.get(id);
    return card ? [card] : [];
  });
  const failedArchive = archives.find((query) => query.isError);
  const loadingArchive = archives.some((query) => query.isPending);
  const missingArchive = olderApproved.some((creative, index) => archives[index]?.isSuccess
    && !archives[index].data?.some((revision) => revision.revision === creative.approved_revision));
  if (!open) return null;
  const channelLabel = channel === 'meta' ? 'Meta' : channel === 'ctv' ? 'CTV' : 'Display';

  const togglePick = (cr: Creative) => {
    if (!multi) {
      onPick([cr]);
      onClose();
      return;
    }
    setSelected(s => {
      const n = new Map(s); if (n.has(cr.id)) n.delete(cr.id); else n.set(cr.id, cr); return n;
    });
    required.clear('library-selection');
  };
  const confirmMulti = () => {
    // Every pick, also those the current search hides: the button counts them all.
    const list = [...selected.values()];
    if (!required.report([
      list.length === 0 && {
        key: 'library-selection',
        message: 'Select at least one creative to insert.',
      },
    ])) return;
    onPick(list);
    onClose();
  };

  return (
    <ResponsiveOverlay label="library picker" kind="form" onClose={onClose} className="modal-host">
      <div className="modal-card" onClick={(e: MouseEvent<HTMLDivElement>)=>e.stopPropagation()} style={{maxWidth:920,maxHeight:'88vh',display:'flex',flexDirection:'column'}}>
        <div className="modal-hd">
          <div>
            <div className="modal-title">Select from Creative Library</div>
            <div className="muted" style={{fontSize:11.5,marginTop:2}}>Filtered to {channelLabel} creatives · {multi ? 'pick one or more' : 'pick one to insert as a new creative'}</div>
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:12,overflow:'hidden',flex:1,minHeight:0}}>
          <div className="flex gap-2 items-center" style={{flexWrap:'wrap'}}>
            <input className="input" placeholder="Search by name…" value={q} onChange={e=>setQ(e.target.value)} style={{flex:'1 1 220px',minWidth:180,fontSize:12}}/>
            <SearchableDropdown
              value={productionFilter}
              onChange={setProductionFilter}
              options={[
                { id: 'all', label: 'All productions' },
                ...productions.map((production) => ({
                  id: production.id,
                  label: production.name,
                })),
              ]}
              searchPlaceholder="Search productions…"
              width={280}
              popWidth={360}
              size="sm"
            />
            <SearchableDropdown
              value={status}
              onChange={setStatus}
              options={[
                { id: 'all', label: 'All statuses' },
                { id: 'approved', label: 'Approved' },
                { id: 'review', label: 'In review' },
                { id: 'draft', label: 'Draft' },
                { id: 'archived', label: 'Archived' },
              ]}
              searchPlaceholder="Search statuses…"
              width={190}
              size="sm"
            />
            <span className="muted mono" style={{fontSize:11,letterSpacing:'.08em'}}>{filtered.length} result{filtered.length===1?'':'s'}</span>
          </div>
          <RequiredField
            fieldKey="library-selection"
            message={required.messages['library-selection']}
            attempt={required.attempt}
            surface
            style={{overflow:'auto',flex:1,minHeight:0,paddingRight:4}}
          >
            {loadingArchive && <div className="muted" role="status">Loading approved creative versions…</div>}
            {failedArchive && <QueryErrorNote error={failedArchive.error} what="approved creative version"
              onRetry={() => { void failedArchive.refetch(); }} />}
            {missingArchive && <div role="alert">An approved creative version could not be read. Refresh the library before selecting it.</div>}
            {filtered.length === 0 ? (
              <div className="muted" style={{padding:'32px 8px',textAlign:'center',fontSize:12}}>No {channelLabel} creatives match these filters.</div>
            ) : (
              <div className="grid-3" style={{gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
                {shown.map(cr => {
                  // Show the creative's own artwork. The production name stays as
                  // the fallback for local dev, where `view_url` is absent because
                  // the R2 S3 secrets are unset.
                  const sh = productionMap[cr.production_id];
                  const posterBg = '#333';
                  const posterFg = '#fff';
                  const posterText = sh?.name || cr.production_id;
                  const pick = pickerPreview(cr);
                  const isSel = selected.has(cr.id);
                  return (
                    <div key={cr.id} className={`panel cr-card ${isSel ? 'cr-card-selected' : ''}`} onClick={()=>togglePick(cr)} style={{overflow:'hidden',cursor:'pointer',outline: isSel ? '2px solid var(--accent)' : 'none',outlineOffset:-2}}>
                      <div className="poster" style={{background:posterBg,color:posterFg,aspectRatio:'4/3',position:'relative'}}>
                        {pick ? (
                          pick.mediaKind === 'video'
                            ? <video src={pick.url} muted loop playsInline className="poster-media"/>
                            : <img src={pick.url} alt="" loading="lazy" className="poster-media"/>
                        ) : (
                          <>
                            <div className="stripes"/>
                            <div className="pt" style={{fontSize:14,color:posterFg}}>{posterText}</div>
                          </>
                        )}
                        <span className="tag" style={{position:'absolute',top:6,left:6,background:'rgba(0,0,0,.55)',color:'#fff',borderColor:'transparent'}}>{cr.format}</span>
                        {cr.media_type === 'video' && (
                          <div style={{position:'absolute',bottom:6,right:6,fontFamily:'var(--font-mono)',fontSize:9.5,color:'#fff',background:'rgba(0,0,0,.55)',padding:'2px 6px',letterSpacing:'.08em'}}>▶ VIDEO</div>
                        )}
                        {isSel && <div style={{position:'absolute',top:6,right:6,width:22,height:22,borderRadius:'50%',background:'var(--accent)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700}}>✓</div>}
                      </div>
                      <div style={{padding:10}}>
                        <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:12,letterSpacing:'.02em',textTransform:'uppercase',lineHeight:1.15}}>{cr.name}</div>
                        <div className="muted mt-1" style={{fontSize:10.5,fontFamily:'var(--font-mono)',letterSpacing:'.05em'}}>{creativeSpecDisplay(cr)}</div>
                        <div className="flex between mt-2" style={{fontFamily:'var(--font-mono)',fontSize:10.5,color:'var(--ink-2)',gap:6,flexWrap:'wrap'}}>
                          <span style={{display:'inline-flex',gap:4}}>
                            {cr.approved_revision != null && <span className="tag tag-version">v{cr.approved_revision}</span>}
                            {cr.approved_revision !== cr.revision && <span className="tag">v{cr.revision} in draft</span>}
                            {cr.approved_revision == null && cr.revision === 1 && '—'}
                          </span>
                          <span style={{textTransform:'uppercase',color:cr.status==='approved'?'var(--good)':cr.status==='review'?'var(--warn)':'var(--ink-3)'}}>{cr.status}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </RequiredField>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {multi && (
            <button className="btn btn-primary" onClick={confirmMulti}>
              Insert {selected.size} creative{selected.size===1?'':'s'}
            </button>
          )}
        </div>
      </div>
    </ResponsiveOverlay>
  );
}

export { LibraryPicker };
